import type { WorkflowGraph } from '../../graph';
import type {
  MaterialNodeSpec,
  OperationNodeSpec,
  FlowEdgeSpec,
  IOMapping,
} from '../../graph/domain/types';
import type { Store } from '../types';
import { pushUndo } from './historyActions';
import { withSyncGuard } from '../sync/syncFlag';
import { recomputeDerivation } from '../recomputeDerivation';
import { isRootMaterial } from '../../graph/domain/topology';
import { propagateDownstream } from '@/commands/helpers/propagateDownstream';
import { cloneGraphData } from './snapshots';
import { generateGuid } from '../../graph/utils/id';
import {
  buildInputReminderNode,
  validateIOMapping,
} from './complexHelpers';
import { propagateComplexCompositionWithReminders } from '../compositionService';
import {
  type TransferRowFormState,
  type NewDestinationDraft,
  buildSourcePostState,
  buildDestinationPostState,
  quantityTypeFromUnit,
} from './transferHelpers';

type SetState = (partial: Partial<Store> | ((s: Store) => Partial<Store>)) => void;
type GetState = () => Store;

// ── Public types ────────────────────────────────────────────────────────────

export interface EditTransferStepInput {
  operationId: string;
  row: TransferRowFormState;
  /** New destination material (replaces old). If same as before, no rewiring needed. */
  destinationMaterialId?: string;
  destinationDraft?: NewDestinationDraft;
  patch?: Partial<OperationNodeSpec>;
}

export interface EditComplexStepInput {
  operationId: string;
  sourceMaterialId: string;
  additionalInputIds: string[];
  outputMaterialIds: string[];
  ioMapping: IOMapping[];
  patch?: Partial<OperationNodeSpec>;
}

export function createEditActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  function toSnapshot(graphData: Store['graphData']) {
    return {
      materialNodes: Object.values(graphData.materialNodes),
      operationNodes: Object.values(graphData.operationNodes),
      flowEdges: Object.values(graphData.flowEdges),
      groups: Object.values(graphData.groups),
      schemaVersion: '2.0' as const,
    };
  }

  function uniqueInOrder(ids: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const id of ids) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
  }

  // Deep equality comparison for objects
  function deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b) return a === b;
    if (typeof a !== 'object' || typeof b !== 'object') return a === b;
    
    const keysA = Object.keys(a).sort();
    const keysB = Object.keys(b).sort();
    
    if (keysA.length !== keysB.length) return false;
    if (!keysA.every((k, i) => k === keysB[i])) return false;
    
    return keysA.every((k) => deepEqual(a[k], b[k]));
  }

  return {
    editMaterial(materialId: string, patch: Partial<MaterialNodeSpec>): void {
      const state = get();
      const material = state.graphData.materialNodes[materialId];
      if (!material) throw new Error(`Material not found: ${materialId}`);
      if (!isRootMaterial(materialId, state.graphData.flowEdges)) return;

      pushUndo(get, set, `Edit ${material.name ?? 'material'}`);

      let changedMaterialIds = new Set<string>();
      set((s) => ({
        graphData: (() => {
          // If name is being updated, also update root material's own constituents
          const updatedPatch = { ...patch };
          const nameChanged = patch.name !== undefined && patch.name !== material.name;
          if (nameChanged) {
            updatedPatch.constituents = [{ name: patch.name!, weight: 1 }];
          }

          const nextGraphData = {
            ...s.graphData,
            materialNodes: {
              ...s.graphData.materialNodes,
              [materialId]: { ...s.graphData.materialNodes[materialId], ...updatedPatch },
            },
          };

          // Only recalculate quantity if the actual value changed (compare only value.amount)
          const currentAmount = material.quantity?.value?.amount;
          const patchAmount = patch.quantity?.value?.amount;
          const quantityChanged = patchAmount !== undefined && patchAmount !== currentAmount;

          // Only recalculate container if it actually changed
          const containerChanged = patch.container !== undefined &&
            !deepEqual(patch.container, material.container);

          const propagated = propagateDownstream(nextGraphData, materialId, {
            fields: {
              identity: nameChanged || containerChanged,
              quantity: quantityChanged,
              constituents: !!nameChanged,
            },
            oldGraphData: quantityChanged ? s.graphData : undefined,  // Pass old data for intelligent recalc
          });
          
          changedMaterialIds = propagated.changedMaterialIds;
          return recomputeDerivation(nextGraphData);
        })(),
        ui: { ...s.ui, isDirty: true },
      }));

      withSyncGuard(set, () => {
        const graph = getGraph();
        if (!graph) return;

        const currentMaterial = get().graphData.materialNodes[materialId];
        if (currentMaterial) {
          graph.updateMaterialNode(materialId, {
            name: currentMaterial.name,
            container: currentMaterial.container,
            quantity: currentMaterial.quantity,
            constituents: currentMaterial.constituents,
          });
        }

        for (const changedId of changedMaterialIds) {
          const changedNode = get().graphData.materialNodes[changedId];
          if (!changedNode) continue;

          const cell = graph.getDataModel().getCell(changedId);
          if (!cell) continue;

          graph.updateMaterialNode(changedId, {
            name: changedNode.name,
            container: changedNode.container,
            quantity: changedNode.quantity,
            constituents: changedNode.constituents,
          });
        }
      });
    },

    editStep(operationId: string, patch: Partial<OperationNodeSpec>): void {
      const state = get();
      const operation = state.graphData.operationNodes[operationId];
      if (!operation) throw new Error(`Operation not found: ${operationId}`);

      pushUndo(get, set, `Edit ${operation.displayName ?? 'operation'}`);

      set((s) => {
        const merged = { ...s.graphData.operationNodes[operationId], ...patch };

        // A patch that flips the archetype must not leave the previous
        // archetype's fields behind. graph/domain/operationNode.ts already does
        // this, but only for COMPLEX ops — everything else is collapsed into an
        // edge and never reaches that code. Structural archetype changes go
        // through changeOperationArchetype; this is the backstop.
        if (patch.archetype && patch.archetype !== operation.archetype) {
          if (patch.archetype !== 'TRANSFER') {
            delete merged.transferRow;
            delete merged.transferOptions;
          }
          if (patch.archetype !== 'COMPLEX') {
            delete merged.complexOptions;
          }
        }

        return {
          graphData: {
            ...s.graphData,
            operationNodes: { ...s.graphData.operationNodes, [operationId]: merged },
          },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        const op = get().graphData.operationNodes[operationId];
        if (!op) return;
        const graph = getGraph();
        if (!graph) return;

        // Only a COMPLEX operation owns a vertex cell. Calling
        // updateOperationNode on an edge-collapsed op throws CellNotFoundError,
        // which is exactly what happened when a patch flipped the archetype to
        // COMPLEX without creating the vertex.
        if (op.archetype === 'COMPLEX' && graph.getDataModel().getCell(operationId)) {
          graph.updateOperationNode(operationId, patch);
        } else if (op.archetype !== 'COMPLEX' && patch.displayName !== undefined) {
          graph.updateFlowEdgeLabel(operationId, patch.displayName);
        }
      });
    },

    editComplexStep(input: EditComplexStepInput): void {
      const state = get();
      const operation = state.graphData.operationNodes[input.operationId];
      if (!operation) throw new Error(`Operation not found: ${input.operationId}`);
      if (operation.archetype !== 'COMPLEX') {
        throw new Error(`Operation ${input.operationId} is not a COMPLEX operation`);
      }

      const source = state.graphData.materialNodes[input.sourceMaterialId];
      if (!source) {
        throw new Error(`Source material not found: ${input.sourceMaterialId}`);
      }

      const normalizedInputIds = uniqueInOrder([
        input.sourceMaterialId,
        ...(input.additionalInputIds ?? []),
      ]);
      const normalizedOutputIds = uniqueInOrder(input.outputMaterialIds ?? []);

      if (normalizedOutputIds.length === 0) {
        throw new Error('At least one output material is required.');
      }

      for (const inputId of normalizedInputIds) {
        if (!state.graphData.materialNodes[inputId]) {
          throw new Error(`Input material not found: ${inputId}`);
        }
      }

      for (const outputId of normalizedOutputIds) {
        const outputNode = state.graphData.materialNodes[outputId];
        if (!outputNode) {
          throw new Error(`Output material not found: ${outputId}`);
        }
        if (outputNode.isReminder || outputNode.symbolicQuantity === 'remainder') {
          throw new Error(`Output material cannot be a reminder: ${outputId}`);
        }
      }

      const mappingValidationError = validateIOMapping(input.ioMapping ?? []);
      if (mappingValidationError) {
        throw new Error(mappingValidationError);
      }

      pushUndo(get, set, `Edit ${operation.displayName ?? 'complex operation'}`);

      const oldGraphData = cloneGraphData(state.graphData);
      const nextGraphData = cloneGraphData(state.graphData);

      const inputEdgeEntries = Object.entries(nextGraphData.flowEdges).filter(
        ([, edge]) => edge.targetId === input.operationId && !!nextGraphData.materialNodes[edge.sourceId],
      );
      const outputEdgeEntries = Object.entries(nextGraphData.flowEdges).filter(
        ([, edge]) => edge.sourceId === input.operationId && !!nextGraphData.materialNodes[edge.targetId],
      );

      const isReminderOutput = (materialId: string): boolean => {
        const node = nextGraphData.materialNodes[materialId];
        if (!node) return false;
        return !!(node.isReminder || node.symbolicQuantity === 'remainder');
      };

      const oldInputIds = inputEdgeEntries.map(([, edge]) => edge.sourceId);
      const oldReminderIds = outputEdgeEntries
        .map(([, edge]) => edge.targetId)
        .filter((materialId) => isReminderOutput(materialId));
      
      // Preserve all old unmapped outputs (non-reminders) independent of ioMapping
      const oldUnmappedOutputIds = outputEdgeEntries
        .map(([, edge]) => edge.targetId)
        .filter((materialId) => !isReminderOutput(materialId));

      const oldInputToReminder = new Map<string, string>();
      for (let i = 0; i < oldInputIds.length && i < oldReminderIds.length; i += 1) {
        oldInputToReminder.set(oldInputIds[i], oldReminderIds[i]);
      }

      const newInputSet = new Set(normalizedInputIds);
      const newOutputSet = new Set([...normalizedOutputIds, ...oldUnmappedOutputIds]);

      for (const [edgeId, edge] of inputEdgeEntries) {
        if (!newInputSet.has(edge.sourceId)) {
          delete nextGraphData.flowEdges[edgeId];
        }
      }

      for (const inputId of normalizedInputIds) {
        const exists = Object.values(nextGraphData.flowEdges).some(
          (edge) => edge.sourceId === inputId && edge.targetId === input.operationId,
        );
        if (!exists) {
          const edgeId = generateGuid();
          nextGraphData.flowEdges[edgeId] = {
            id: edgeId,
            edgeId,
            sourceId: inputId,
            targetId: input.operationId,
          };
        }
      }

      for (const [edgeId, edge] of outputEdgeEntries) {
        if (!isReminderOutput(edge.targetId) && !newOutputSet.has(edge.targetId)) {
          delete nextGraphData.flowEdges[edgeId];
        }
      }

      for (const outputId of normalizedOutputIds) {
        const exists = Object.values(nextGraphData.flowEdges).some(
          (edge) => edge.sourceId === input.operationId && edge.targetId === outputId,
        );
        if (!exists) {
          const edgeId = generateGuid();
          nextGraphData.flowEdges[edgeId] = {
            id: edgeId,
            edgeId,
            sourceId: input.operationId,
            targetId: outputId,
          };
        }
      }

      const reusedReminderIds = new Set<string>();
      const reminderIdsInInputOrder: string[] = [];
      for (const inputId of normalizedInputIds) {
        const reminderId = oldInputToReminder.get(inputId);
        if (reminderId && nextGraphData.materialNodes[reminderId]) {
          reusedReminderIds.add(reminderId);
          reminderIdsInInputOrder.push(reminderId);
        } else {
          reminderIdsInInputOrder.push('');
        }
      }

      for (const reminderId of oldReminderIds) {
        if (reusedReminderIds.has(reminderId)) continue;

        for (const [edgeId, edge] of Object.entries(nextGraphData.flowEdges)) {
          if (edge.sourceId === reminderId || edge.targetId === reminderId) {
            delete nextGraphData.flowEdges[edgeId];
          }
        }
        delete nextGraphData.materialNodes[reminderId];
      }

      const operationPos = operation.position ?? { x: source.position?.x ?? 0, y: source.position?.y ?? 0 };
      const reminderSpreadDx = 80;
      const reminderY = operationPos.y + 120;
      const reminderStartX = operationPos.x - (normalizedInputIds.length * reminderSpreadDx) / 2;

      for (let i = 0; i < normalizedInputIds.length; i += 1) {
        const inputId = normalizedInputIds[i];
        const inputMaterial = nextGraphData.materialNodes[inputId];
        let reminderId = reminderIdsInInputOrder[i];

        if (!reminderId) {
          reminderId = generateGuid();
          reminderIdsInInputOrder[i] = reminderId;
          const reminderSpec = buildInputReminderNode(inputMaterial, input.operationId);
          nextGraphData.materialNodes[reminderId] = {
            ...reminderSpec,
            id: reminderId,
            vertexId: reminderId,
            createdAt: new Date().toISOString(),
            position: {
              x: reminderStartX + i * reminderSpreadDx,
              y: reminderY,
            },
          };
        } else {
          const existingReminder = nextGraphData.materialNodes[reminderId];
          nextGraphData.materialNodes[reminderId] = {
            ...existingReminder,
            isReminder: true,
            symbolicQuantity: 'remainder',
            sourceMaterials: [
              {
                sourceVertexId: inputId,
                name: inputMaterial.name,
                container: inputMaterial.container,
                notes: null,
              },
            ],
          };
        }

        const hasReminderEdge = Object.values(nextGraphData.flowEdges).some(
          (edge) => edge.sourceId === input.operationId && edge.targetId === reminderId,
        );
        if (!hasReminderEdge) {
          const edgeId = generateGuid();
          nextGraphData.flowEdges[edgeId] = {
            id: edgeId,
            edgeId,
            sourceId: input.operationId,
            targetId: reminderId,
          };
        }
      }

      nextGraphData.operationNodes[input.operationId] = {
        ...operation,
        ...input.patch,
        complexOptions: {
          ...(operation.complexOptions ?? {}),
          ioMapping: input.ioMapping ?? [],
        },
      };

      const inputNames = normalizedInputIds.map((materialId) => nextGraphData.materialNodes[materialId]?.name ?? '');
      const { outputComposition, reminderCompositions } = propagateComplexCompositionWithReminders(
        normalizedInputIds,
        inputNames,
        input.operationId,
      );

      for (const outputId of normalizedOutputIds) {
        const currentOutput = nextGraphData.materialNodes[outputId];
        if (!currentOutput) continue;
        nextGraphData.materialNodes[outputId] = {
          ...currentOutput,
          composition: outputComposition,
        };
      }

      for (let i = 0; i < reminderIdsInInputOrder.length; i += 1) {
        const reminderId = reminderIdsInInputOrder[i];
        const reminderNode = nextGraphData.materialNodes[reminderId];
        if (!reminderNode) continue;
        nextGraphData.materialNodes[reminderId] = {
          ...reminderNode,
          composition: reminderCompositions[i],
        };
      }

      const changedMaterialIds = new Set<string>();
      for (const inputId of normalizedInputIds) {
        const result = propagateDownstream(nextGraphData, inputId, {
          fields: { identity: true, quantity: true, constituents: true },
          oldGraphData,
        });
        for (const changedId of result.changedMaterialIds) {
          changedMaterialIds.add(changedId);
        }
      }

      const recomputed = recomputeDerivation(nextGraphData);

      set((s) => ({
        graphData: recomputed,
        selection: {
          cellIds: s.selection.cellIds.filter((id) => !!recomputed.materialNodes[id] || !!recomputed.operationNodes[id] || !!recomputed.flowEdges[id] || !!recomputed.groups[id]),
        },
        ui: { ...s.ui, isDirty: true },
      }));

      withSyncGuard(set, () => {
        const graph = getGraph();
        if (!graph) return;
        graph.hydrateFromSnapshot(toSnapshot(get().graphData));
      });
    },

    /**
     * Edit a TRANSFER step with optional destination change.
     * Handles edge rewiring when the destination material is changed.
     */
    editTransferStep(input: EditTransferStepInput): void {
      const state = get();
      const operation = state.graphData.operationNodes[input.operationId];
      if (!operation) throw new Error(`Operation not found: ${input.operationId}`);
      if (operation.archetype !== 'TRANSFER') {
        throw new Error(`Operation ${input.operationId} is not a TRANSFER`);
      }

      const oldDestId = operation.transferRow?.destinationMaterialId;
      const newDestId = input.destinationMaterialId ?? null;
      const destChanged =
        (newDestId != null && newDestId !== oldDestId) ||
        (input.destinationDraft != null && oldDestId != null);

      pushUndo(get, set, `Edit transfer ${operation.displayName ?? ''}`);

      // Find the source material (input edge where target is this op)
      const edges = state.graphData.flowEdges;
      const inputEdges = Object.entries(edges).filter(
        ([, e]) => e.targetId === input.operationId,
      );
      // Source material is the one that is NOT the old destination
      const sourceEdgeEntry = inputEdges.find(
        ([, e]) => e.sourceId !== oldDestId,
      ) ?? inputEdges[0];
      const sourceMaterialId =
        operation.transferRow?.sourceMaterialId ??
        sourceEdgeEntry?.[1]?.sourceId;
      const source = sourceMaterialId
        ? state.graphData.materialNodes[sourceMaterialId]
        : null;

      if (!source) throw new Error('Cannot find source material for transfer');

      // Resolve new destination
      let resolvedDestId: string;
      let resolvedDest: MaterialNodeSpec | null;

      if (input.destinationDraft) {
        resolvedDestId = get().addMaterialNode({
          name: input.destinationDraft.name,
          container: input.destinationDraft.container,
          constituents: [],
          quantity: null,
          isEmpty: true,
        });
        resolvedDest = null;
      } else if (newDestId) {
        resolvedDestId = newDestId;
        resolvedDest = state.graphData.materialNodes[newDestId] ?? null;
      } else {
        // Destination unchanged
        resolvedDestId = oldDestId!;
        resolvedDest = oldDestId
          ? state.graphData.materialNodes[oldDestId] ?? null
          : null;
      }

      // If destination changed, rewire edges
      if (destChanged && oldDestId) {
        // Remove old destination→op edge (if it existed)
        const oldDestEdge = Object.entries(edges).find(
          ([, e]) => e.sourceId === oldDestId && e.targetId === input.operationId,
        );
        if (oldDestEdge) {
          get().removeFlowEdge(oldDestEdge[0]);
        }

        // Find the old destination post-state material and its edge
        const outputEdges = Object.entries(get().graphData.flowEdges).filter(
          ([, e]) => e.sourceId === input.operationId,
        );

        // Identify which output is the destination post-state (not the source post-state)
        // The source post-state has the same name as the source material
        const destPostEdge = outputEdges.find(([, e]) => {
          const mat = get().graphData.materialNodes[e.targetId];
          return mat && mat.name !== source.name;
        }) ?? outputEdges[1]; // fallback: second output

        // Update the destination post-state material
        if (destPostEdge) {
          const destPostId = destPostEdge[1].targetId;
          const newDestPost = buildDestinationPostState(
            source,
            resolvedDest,
            input.row,
            resolvedDestId,
          );
          get().updateMaterialNode(destPostId, newDestPost);
        }

        // Add new destination→op edge if existing dest
        if (resolvedDest) {
          get().connectMaterialToOperation(resolvedDestId, input.operationId);
        }
      }

      // Update source post-state material (quantity may have changed)
      const currentOutputEdges = Object.entries(get().graphData.flowEdges).filter(
        ([, e]) => e.sourceId === input.operationId,
      );
      const sourcePostEdge = currentOutputEdges.find(([, e]) => {
        const mat = get().graphData.materialNodes[e.targetId];
        return mat && mat.name === source.name;
      }) ?? currentOutputEdges[0];

      if (sourcePostEdge) {
        const sourcePostId = sourcePostEdge[1].targetId;
        const newSourcePost = buildSourcePostState(source, input.row);
        // Only update quantity/constituents, not position
        set((s) => ({
          graphData: {
            ...s.graphData,
            materialNodes: {
              ...s.graphData.materialNodes,
              [sourcePostId]: {
                ...s.graphData.materialNodes[sourcePostId],
                quantity: newSourcePost.quantity,
                constituents: newSourcePost.constituents,
                symbolicQuantity: newSourcePost.symbolicQuantity,
              },
            },
          },
        }));
      }

      // If dest didn't change, still update dest post-state quantity
      if (!destChanged) {
        const destPostEdge = currentOutputEdges.find(([, e]) => {
          const mat = get().graphData.materialNodes[e.targetId];
          return mat && mat.name !== source.name;
        }) ?? currentOutputEdges[1];

        if (destPostEdge) {
          const destPostId = destPostEdge[1].targetId;
          const newDestPost = buildDestinationPostState(
            source,
            resolvedDest,
            input.row,
            resolvedDestId,
          );
          set((s) => ({
            graphData: {
              ...s.graphData,
              materialNodes: {
                ...s.graphData.materialNodes,
                [destPostId]: {
                  ...s.graphData.materialNodes[destPostId],
                  quantity: newDestPost.quantity,
                  constituents: newDestPost.constituents,
                  symbolicQuantity: newDestPost.symbolicQuantity,
                },
              },
            },
          }));
        }
      }

      // Resolve post-state material ids for the rebuilt transferRow. Prefer
      // the ids already stored on the operation (post materials are updated
      // in place, so they stay valid); fall back to the name heuristic.
      const finalOutputEdges = Object.entries(get().graphData.flowEdges).filter(
        ([, e]) => e.sourceId === input.operationId,
      );
      const finalSourcePostEdge = finalOutputEdges.find(([, e]) => {
        const mat = get().graphData.materialNodes[e.targetId];
        return mat && mat.name === source.name;
      }) ?? finalOutputEdges[0];
      const finalDestPostEdge = finalOutputEdges.find(([, e]) => {
        const mat = get().graphData.materialNodes[e.targetId];
        return mat && mat.name !== source.name;
      }) ?? finalOutputEdges[1];

      // Update the operation spec
      const transferRow = {
        id: input.row.id,
        sourceMaterialId,
        destinationMaterialId: resolvedDestId,
        sourcePostMaterialId:
          operation.transferRow?.sourcePostMaterialId ??
          finalSourcePostEdge?.[1]?.targetId,
        destinationPostMaterialId:
          operation.transferRow?.destinationPostMaterialId ??
          finalDestPostEdge?.[1]?.targetId,
        quantity:
          input.row.mode === 'numeric' &&
          input.row.amount != null &&
          input.row.unit != null
            ? {
                value: { amount: input.row.amount, units: input.row.unit },
                type: quantityTypeFromUnit(input.row.unit),
              }
            : undefined,
        symbolic:
          input.row.mode === 'symbolic'
            ? (input.row.symbolic ?? undefined)
            : undefined,
      };

      const patch = { ...input.patch };
      if (input.row.amount === 0 || (input.row.amount != null && input.row.amount <= 0)) {
        const transferOptions = patch.transferOptions ?? operation.transferOptions;
        if (transferOptions) {
          patch.transferOptions = {
            ...transferOptions,
            transferEntireVolume: false,
          };
        }
      }

      set((s) => ({
        graphData: {
          ...s.graphData,
          operationNodes: {
            ...s.graphData.operationNodes,
            [input.operationId]: {
              ...s.graphData.operationNodes[input.operationId],
              ...patch,
              transferRow,
            },
          },
        },
        ui: { ...s.ui, isDirty: true },
      }));
    },
  };
}
